import React from 'react'
import Pagination from 'rc-pagination'
import 'rc-pagination/assets/index.css'
import { Box, MenuItem, Typography, type SelectChangeEvent, useMediaQuery } from '@mui/material'
import GenericButton from '../../GenericButton'
import { ButtonSize, ButtonType } from '../../GenericButton/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { PaginationWrapper, SelectPageSize } from './styles'
import { grey, shade } from '../../../colors'
import { useTheme } from '@mui/material/styles'
import { type IPagination } from '../../../models/DataObject'

const pageSizes = [5, 10, 20]

interface CustomPaginationProps extends IPagination {
  handlePageChange: (page: number) => Promise<void> | void
  handleRowsPerPageChange?: (pageSize: number) => Promise<void> | void
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  totalResults,
  numPages,
  pageNumber,
  pageSize,
  handlePageChange = () => { },
  handleRowsPerPageChange = () => { }
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handlePageSizeChange = (event: SelectChangeEvent<unknown>): void => {
    const newPageSize = Number(event.target.value)
    void handleRowsPerPageChange(newPageSize)
  }

  const pageChange = (page: number): void => {
    void handlePageChange(page)
  }

  return (
    <PaginationWrapper spacing={2}>
      <GenericButton
        buttonType={ButtonType.Secondary}
        buttonSize={ButtonSize.Small}
        onClick={() => { pageChange(pageNumber - 1) }}
        disabled={pageNumber === 1}
        fontWeight='bold'
        icon={isMobile ? <ArrowBackIcon /> : null}
        label={!isMobile ? 'Previous' : undefined}
        style={{
          width: isMobile ? '36px' : 'auto'
        }}
      />

      {
        isMobile
          ? <Typography color={grey[950]} fontWeight={500} fontSize='14px'> Page {pageNumber} of {numPages}</Typography>
          : <Pagination
            pageSize={pageSize}
            total={totalResults}
            current={pageNumber}
            onChange={(page) => { pageChange(page) }}
            showLessItems
            showTitle={false}
            itemRender={(page, type, element) => {
              switch (type) {
                case 'prev':
                case 'next':
                  return null
                case 'page':
                  return page
                default:
                  return element
              }
            }}
          />
      }

      <Box display='flex' gap={4}>
        {!isMobile &&
          <SelectPageSize
            size='small'
            IconComponent={KeyboardArrowDownIcon}
            value={pageSize}
            defaultValue={pageSize}
            onChange={handlePageSizeChange}
            renderValue={(value: unknown) => `Rows (${value as number})`}
            sx={{
              minWidth: `${theme.spacing(34)} !important`
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  fontSize: '14px',
                  color: grey[950],
                  fontWeight: 500,
                  border: `1px solid ${shade[650]}`,
                  maxWidth: `${theme.spacing(20)} !important`,
                  minWidth: `${theme.spacing(20)} !important`,
                  width: `${theme.spacing(20)} !imporntant`,
                  boxShadow: '0px 4px 6px -2px rgba(32, 41, 57, 0.03), 0px 12px 16px -4px rgba(32, 41, 57, 0.08)'
                }
              }
            }}
          >
            {pageSizes.map((size) => (
              <MenuItem key={size} value={size} style={{ padding: `${theme.spacing(2)}  ${theme.spacing(4)}`, fontWeight: 500, fontSize: '14px' }}>
                {size}
              </MenuItem>
            ))}
          </SelectPageSize>
        }

        <GenericButton
          buttonType={ButtonType.Secondary}
          buttonSize={ButtonSize.Small}
          onClick={() => { pageChange(pageNumber + 1) }}
          disabled={pageNumber === numPages}
          fontWeight='bold'
          icon={isMobile ? <ArrowForwardIcon /> : null}
          label={!isMobile ? 'Next' : undefined}
          style={{
            width: isMobile ? '36px' : 'auto'
          }}
        />
      </Box>
    </PaginationWrapper>
  )
}

export default CustomPagination
